import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'

import { Table } from '@/components/Table'
import { Alignment } from '@/components/Table/tableHelper'
import { SortOrder } from '@/types/common'
import { cn, formatNumber } from '@/utils'
import { SortDirection } from '@/utils/constants'

import { containerVariants } from '../../constants'
import { useMostActiveUsers } from '../../hooks/useMostActiveUsers'

// Helper function to create numeric column with formatting
const createNumericColumn = (
  id: string,
  label: string,
  width: number = 150
) => ({
  id,
  label,
  width,
  align: Alignment.RIGHT,
  alignLabel: Alignment.RIGHT,
  sortable: true,
  renderCell: (value: unknown) => <span>{formatNumber(value as number)}</span>,
})

// Helper function to compare values for sorting
const compareValues = (
  valA: unknown,
  valB: unknown,
  direction: SortOrder
): number => {
  if (typeof valA === 'string' && typeof valB === 'string') {
    return direction === SortDirection.ASC
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA)
  }

  if (typeof valA === 'number' && typeof valB === 'number') {
    return direction === SortDirection.ASC ? valA - valB : valB - valA
  }

  return 0
}

export const MostActiveUser: React.FC = () => {
  const { t } = useTranslation()
  const { data, isLoading } = useMostActiveUsers()

  const [orderBy, setOrderBy] = useState<{
    column: string
    direction: SortOrder
  }>({ column: '', direction: null })

  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px 0px' })

  // Define table columns
  const columns = useMemo(
    () => [
      {
        id: 'row_number',
        label: t('dashboard.mostActiveUsers.columns.no'),
        width: 80,
        align: Alignment.CENTER,
        alignLabel: Alignment.CENTER,
        sortable: false,
        renderCell: (value: unknown) => (
          <span className="font-medium">{value as number}</span>
        ),
      },
      {
        id: 'full_name',
        label: t('dashboard.mostActiveUsers.columns.userName'),
        width: 200,
        align: Alignment.LEFT,
        alignLabel: Alignment.LEFT,
        sortable: true,
        renderCell: (value: unknown) => <span>{value as string}</span>,
      },
      createNumericColumn(
        'no_of_views',
        t('dashboard.mostActiveUsers.columns.fileViews')
      ),
      createNumericColumn(
        'no_of_comments',
        t('dashboard.mostActiveUsers.columns.comments')
      ),
      createNumericColumn(
        'no_of_replies',
        t('dashboard.mostActiveUsers.columns.replies')
      ),
      createNumericColumn(
        'no_of_status_changes',
        t('dashboard.mostActiveUsers.columns.statusChanges'),
        180
      ),
    ],
    [t]
  )

  // Sort data based on current sort settings and add row numbers
  const sortedData = useMemo(() => {
    let result = data

    // Only sort if a column direction is specified
    if (orderBy.column && orderBy.direction) {
      result = [...data].sort((a, b) => {
        const valA = (a as unknown as Record<string, unknown>)[orderBy.column]
        const valB = (b as unknown as Record<string, unknown>)[orderBy.column]
        return compareValues(valA, valB, orderBy.direction)
      })
    }

    // Add row numbers to the data
    return result.map((item, index) => ({
      ...item,
      row_number: index + 1,
    }))
  }, [data, orderBy.column, orderBy.direction])

  // Handle sort change
  const handleSortChange = (colId: string | null, dir: SortOrder) => {
    if (!colId || !dir) {
      setOrderBy({ column: '', direction: null })
      return
    }
    setOrderBy({ column: colId, direction: dir })
  }

  return (
    <motion.div
      ref={ref}
      className={cn('mt-8 space-y-4')}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="flex items-center gap-2">
        <h2 className="font-semibold text-lg text-primary-900 m-0">
          {t('dashboard.mostActiveUsers.title')}
        </h2>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <Table
          columns={columns}
          data={sortedData}
          isLoading={isLoading}
          orderBy={orderBy.column as never}
          order={orderBy.direction}
          onSortChange={handleSortChange}
          roundedTop
          roundedBottom
        />
      </div>
    </motion.div>
  )
}
